import { useCallback, useEffect, useRef, useState } from "react";
import { readRecoveryDelay, request } from "../api/client";
import { parseWorkspacePage, workspaceKey, type ProductWorkspace } from "./workspaces";
import { productStep } from "./audit";

export function useProductWorkspaces(enabled = true, revision = 0) {
  const [items, setItems] = useState<ProductWorkspace[] | null>(null);
  const [error, setError] = useState<unknown>();
  const [generation, setGeneration] = useState(0);
  const recoveryAttempts = useRef(0);
  const refresh = useCallback(() => setGeneration((n) => n + 1), []);
  useEffect(() => {
    if (!enabled) { setItems(null); setError(null); return; }
    const controller = new AbortController();
    setError(null);
    async function load() {
      productStep("workspaces", "started");
      let cursor = "";
      const cursors = new Set<string>();
      const found = new Map<string, ProductWorkspace>();
      do {
        if (cursors.has(cursor) || cursors.size >= 100) throw new Error("Invalid workspace pagination");
        cursors.add(cursor);
        const page = parseWorkspacePage(await request<unknown>(
          "/productWorkspaces" + (cursor ? "?" + new URLSearchParams({ cursor }) : ""),
          "GET", undefined, AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]),
        ));
        for (const item of page.items) found.set(workspaceKey(item), item);
        cursor = page.nextCursor || "";
      } while (cursor && !controller.signal.aborted);
      if (!controller.signal.aborted) { recoveryAttempts.current = 0; setItems([...found.values()]); productStep("workspaces", "succeeded", { count: found.size }); }
    }
    void load().catch((e) => {
      if (!controller.signal.aborted) { setItems(null); setError(e); productStep("workspaces", "failed", {}, e); }
    });
    return () => controller.abort();
  }, [enabled, revision, generation]);
  useEffect(() => {
    if (!enabled || !error) return;
    const delay = readRecoveryDelay(error, recoveryAttempts.current);
    if (delay === undefined) return;
    const timer = window.setTimeout(() => { recoveryAttempts.current++; refresh(); }, delay);
    return () => clearTimeout(timer);
  }, [enabled, error, refresh]);
  useEffect(() => {
    if (!enabled) return;
    // Refresh discovery for access/billing changes, not ordinary window focus or time.
    const revalidate = () => { setItems(current => current && [...current]); refresh(); };
    const invalidateBillingAccess = () => { setItems(null); refresh(); };
    window.addEventListener("online", revalidate);
    window.addEventListener("mzn:access-check", revalidate);
    window.addEventListener("mzn:billing-changed", invalidateBillingAccess);
    return () => {
      window.removeEventListener("online", revalidate);
      window.removeEventListener("mzn:access-check", revalidate);
      window.removeEventListener("mzn:billing-changed", invalidateBillingAccess);
    };
  }, [enabled, refresh]);
  useEffect(() => {
    if (!enabled || !items) return;
    const nextExpiry = Math.min(...items.filter(item => item.enabled && item.subscriptionStatus === "ACTIVE" && !item.freeForLife &&
      typeof item.expiresAtEpochMillis === "number" && item.expiresAtEpochMillis > Date.now()).map(item => item.expiresAtEpochMillis!));
    if (!Number.isFinite(nextExpiry)) return;
    const timer = window.setTimeout(() => {
      // Re-render immediately: an open editor must disappear before the network recheck completes.
      setItems(current => current && [...current]);
      refresh();
      window.dispatchEvent(new Event("mzn:access-check"));
    }, Math.min(2_147_483_647, Math.max(1, nextExpiry - Date.now())));
    return () => clearTimeout(timer);
  }, [enabled, items, refresh]);
  return { items, error, refresh };
}
